import dayjs from 'dayjs';
import { InboxFutureFilterableNotification, isInboxClinicalTask } from './inbox-tasks';

export const InboxSortMode = {
  NEWEST_FIRST: 'newest-first',
  DUE_DATE: 'due-date',
} as const;

export type InboxSortMode = (typeof InboxSortMode)[keyof typeof InboxSortMode];

export const INBOX_SORT_MODE_LABELS: Record<InboxSortMode, string> = {
  [InboxSortMode.NEWEST_FIRST]: 'Newest first',
  [InboxSortMode.DUE_DATE]: 'Due date',
};

const INBOX_SORT_MODE_VALUES = new Set<string>(Object.values(InboxSortMode));

export function isInboxSortMode(value: unknown): value is InboxSortMode {
  return typeof value === 'string' && INBOX_SORT_MODE_VALUES.has(value);
}

export interface InboxSortableNotification extends InboxFutureFilterableNotification {
  id: string;
  timestamp: Date;
}

/** Overdue → today → upcoming; within each bucket, soonest due first. */
const DUE_BUCKET = {
  OVERDUE: 0,
  TODAY: 1,
  UPCOMING: 2,
  NO_DUE: 3,
} as const;

function getClinicalTaskDueBucket(endIso: string): { bucket: number; sortTime: number } {
  const due = dayjs(endIso);
  const now = dayjs();

  if (due.isBefore(now, 'day')) {
    return { bucket: DUE_BUCKET.OVERDUE, sortTime: due.valueOf() };
  }

  if (due.isSame(now, 'day')) {
    return { bucket: DUE_BUCKET.TODAY, sortTime: due.valueOf() };
  }

  return { bucket: DUE_BUCKET.UPCOMING, sortTime: due.valueOf() };
}

function compareDueDateItems(a: InboxSortableNotification, b: InboxSortableNotification): number {
  const aIsTask = isInboxClinicalTask(a.task, a.sourceCommunication);
  const bIsTask = isInboxClinicalTask(b.task, b.sourceCommunication);

  if (aIsTask && !bIsTask) {
    return -1;
  }

  if (!aIsTask && bIsTask) {
    return 1;
  }

  if (!aIsTask && !bIsTask) {
    return b.timestamp.getTime() - a.timestamp.getTime();
  }

  const aEnd = a.task.executionPeriod?.end;
  const bEnd = b.task.executionPeriod?.end;

  if (!aEnd && !bEnd) {
    return 0;
  }

  if (!aEnd) {
    return 1;
  }

  if (!bEnd) {
    return -1;
  }

  const aDue = getClinicalTaskDueBucket(aEnd);
  const bDue = getClinicalTaskDueBucket(bEnd);

  if (aDue.bucket !== bDue.bucket) {
    return aDue.bucket - bDue.bucket;
  }

  if (aDue.bucket === DUE_BUCKET.OVERDUE) {
    return bDue.sortTime - aDue.sortTime;
  }

  return aDue.sortTime - bDue.sortTime;
}

export function sortInboxNotifications<T extends InboxSortableNotification>(items: T[], sortMode: InboxSortMode): T[] {
  if (sortMode === InboxSortMode.NEWEST_FIRST) {
    return [...items].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  return [...items].sort(compareDueDateItems);
}
